import React, { useState, useEffect } from "react";
import { Modal, Box, Button, Typography } from "@mui/material";
import { CloseOutlined, ArrowBack } from "@mui/icons-material";

import TemplatesGrid from "./TemplatesGrid";
import TemplatesPreview from "./TemplatesPreview";
import { useTemplatePreview } from "./useTemplatePreview";
import { useTheme } from "@mui/material/styles";

const TemplatesModal = ({
  templateOptions,
  open,
  onClose,
  updateFieldValue,
  field,
  field_id,
  setError,
  templateType,
}) => {
  const templatesFetched = Array.isArray(templateOptions);
  const rows = templatesFetched ? templateOptions : [];

  const isLoading = open && !templatesFetched;
  const noTemplates = open && templatesFetched && rows.length === 0;

  const [mode, setMode] = useState("list");
  const [previewRow, setPreviewRow] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);

  const selectedRow = rows.find((row) => row.id === selectionModel[0]);

  const preview = useTemplatePreview(
    mode === "preview" && previewRow && rows.length ? [previewRow.id] : [],
    rows
  );

  const theme = useTheme();

  // ✅ Quando abrir, aplica seleção atual (field_id)
  // ✅ Quando fechar, limpa tudo (inclusive seleção)
  useEffect(() => {
  if (!open) return;

  if (field_id) {
    setSelectionModel([field_id]);
  } else {
    setSelectionModel([]);
  }
}, [open, field_id]);


  // Se entrar no preview e rows sumirem, volta pro list
  useEffect(() => {
    if (mode === "preview" && rows.length === 0) {
      setMode("list");
      setPreviewRow(null);
    }
  }, [rows.length, mode]);

  const handleSelect = (row) => {
    if (!row) return;

    updateFieldValue(field, row.id);
    setError?.((prev) => ({ ...prev, [field]: false }));
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "96vw", sm: "90vw", md: "80vw" },
          height: { xs: "96vh", sm: "90vh" },
          backgroundColor: "#F7F7FC",
          borderRadius: 2,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <Box
          px={3}
          py={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {mode === "preview" ? (
            <ArrowBack
              onClick={() => setMode("list")}
              sx={{ cursor: "pointer", color: theme.palette.custom.gray[300] }}
            />
          ) : (
            <Box />
          )}

          <CloseOutlined
            onClick={onClose}
            sx={{ cursor: "pointer", color: theme.palette.custom.gray[300] }}
          />
        </Box>

        {/* BODY */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",

            ...(mode === "preview" && {
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }),

            "&::-webkit-scrollbar": {
              width: 8,
              height: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#C7C7CC",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },

            scrollbarWidth: "thin",
            scrollbarColor: "#C7C7CC transparent",
          }}
        >
          {/* TÍTULO - só no modo list */}
          {mode === "list" && (
            <Box px={3} mb={1}>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 500,
                  color: "#545454",
                }}
              >
                Selecione um modelo
              </Typography>
            </Box>
          )}

          {/* LIST MODE */}
          {mode === "list" ? (
            isLoading ? (
              <Box px={3} py={2}>
                <Typography sx={{ fontSize: "16px", color: "#666" }}>
                  Carregando modelos...
                </Typography>
              </Box>
            ) : noTemplates ? (
              <Box px={3} py={2}>
                <Typography sx={{ fontSize: "16px", color: "#666" }}>
                  Nenhum modelo disponível para este canal.
                </Typography>
              </Box>
            ) : (
              <TemplatesGrid
                rows={rows}
                selectionModel={selectionModel}
                onSelectionChange={setSelectionModel}
                onPreview={(row) => {
                  if (!row) return;
                  setPreviewRow(row);
                  setMode("preview");
                }}
                onSelect={handleSelect}
              />
            )
          ) : (
            <TemplatesPreview templateType={templateType} preview={preview} />
          )}
        </Box>

        {/* FOOTER */}
        <Box
          px={3}
          py={2}
          display="flex"
          justifyContent="flex-end"
          gap={2}
          borderTop="1px solid #E5E5EF"
          bgcolor="#F7F7FC"
        >
          <Button variant="outlined" onClick={onClose}>
            Sair
          </Button>

          <Button
            variant="contained"
            disabled={
              isLoading ||
              noTemplates ||
              !selectedRow ||
              selectedRow.status !== "APPROVED"
            }
            onClick={() => handleSelect(selectedRow)}
          >
            Selecionar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default TemplatesModal;
